import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useSuspenseQuery } from '@apollo/client/react';
import dayjs from 'dayjs';

import { Table, TableHeader, TableHead, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { AssetContentTypeEnum, ContentTypeEnum } from 'src/graphql/schema';
import { BeneficiaryCertificateAssetCountDocument } from 'src/graphql/actions/asset/beneficiaryCertificateAssetCount.query';
import { withSuspense } from 'src/hoc';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import { contentTypeMap } from 'src/containers/SoftwareEscrow/Deposits/model';

import type { IBeneficiaryCertificateAssetCountQuery } from 'src/graphql/actions/asset/beneficiaryCertificateAssetCount.query';

type ITableRow = {
  contentType: AssetContentTypeEnum | null | undefined;
  count: number;
  latestDeposit: string;
};

const columnHelper = createColumnHelper<ITableRow>();

const columns = [
  columnHelper.accessor('contentType', {
    header: 'Asset type',
    cell: (props) => {
      const value = props.getValue();
      return value ? contentTypeMap[value as keyof typeof contentTypeMap] : 'Unlabeled';
    },
  }),
  columnHelper.accessor('count', {
    header: 'Assets',
    cell: (props) => props.getValue(),
  }),
  columnHelper.accessor('latestDeposit', {
    header: 'Date of last deposit',
    cell: (props) => (props.getValue() ? dayjs(props.getValue()).format('MMM DD, YYYY, HH:mm') : '-'),
  }),
];

const ContentTypeCountTable = withSuspense(() => {
  const {
    softwareEscrow: { id: softwareEscrowId, requiredContentTypes },
  } = useSoftwareEscrow();
  const { data: dataUnlabeled } = useSuspenseQuery(BeneficiaryCertificateAssetCountDocument, {
    variables: { softwareEscrowId, labeled: false },
  });
  const { data: dataSourceCode } = useSuspenseQuery(BeneficiaryCertificateAssetCountDocument, {
    variables: { softwareEscrowId, contentType: AssetContentTypeEnum.SourceCode },
    skip: !requiredContentTypes.includes(ContentTypeEnum.SourceCode),
  });
  const { data: dataDocumentation } = useSuspenseQuery(BeneficiaryCertificateAssetCountDocument, {
    variables: { softwareEscrowId, contentType: AssetContentTypeEnum.Documentation },
    skip: !requiredContentTypes.includes(ContentTypeEnum.Documentation),
  });
  const { data: dataData } = useSuspenseQuery(BeneficiaryCertificateAssetCountDocument, {
    variables: { softwareEscrowId, contentType: AssetContentTypeEnum.Data },
    skip: !requiredContentTypes.includes(ContentTypeEnum.Data),
  });

  const assetsData = [
    getAssetData(undefined, dataUnlabeled),
    getAssetData(AssetContentTypeEnum.SourceCode, dataSourceCode),
    getAssetData(AssetContentTypeEnum.Documentation, dataDocumentation),
    getAssetData(AssetContentTypeEnum.Data, dataData),
  ].filter(Boolean) as ITableRow[];

  const table = useReactTable({
    data: assetsData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="border rounded-md overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  className="border-r last:border-r-0 bg-accent px-2 h-[27px] text-[9px] text-foreground font-bold"
                  key={header.id}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="border-r last:border-r-0 p-2 text-[9px] text-foreground bg-white">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="p-2 text-[9px] text-foreground bg-white text-center">
                No assets
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
});

function getAssetData(type?: AssetContentTypeEnum, data?: IBeneficiaryCertificateAssetCountQuery) {
  return data?.beneficiaryCertificateAssetCount.count
    ? {
        contentType: type,
        count: data.beneficiaryCertificateAssetCount.count,
        latestDeposit: data.beneficiaryCertificateAssetCount.latestDeposit,
      }
    : null;
}

export default withSuspense(ContentTypeCountTable);
